import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Collapse,
  IconButton,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LinkIcon from '@mui/icons-material/Link';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import { fetchWithAuth } from '../lib/api';
import { useNavigate } from 'react-router-dom';

const DISMISSED_KEY = 'palmera_onboarding_dismissed';

export default function OnboardingChecklist() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem(DISMISSED_KEY) === 'true';
  });
  const [checks, setChecks] = useState({
    hasMyData: false,
    hasStoreHours: false,
    hasEmployees: false,
    loading: true,
  });

  useEffect(() => {
    if (dismissed) return;
    checkSetupStatus();
  }, [dismissed]);

  const checkSetupStatus = async () => {
    try {
      const [settingsRes, employeesRes] = await Promise.all([
        fetchWithAuth('/api/settings').then((r) => r.json()),
        fetchWithAuth('/api/employees').then((r) => r.json()),
      ]);

      const hasMyData = !!(
        settingsRes.mydata_user_id &&
        settingsRes.mydata_user_id !== '***encrypted***' &&
        settingsRes.mydata_subscription_key &&
        settingsRes.mydata_subscription_key !== '***encrypted***'
      );

      let realMyData = false;
      if (hasMyData) {
        try {
          const uid = await fetchWithAuth('/api/settings/mydata_user_id').then((r) => r.json());
          realMyData = !!(uid.value && uid.value.trim());
        } catch {
          realMyData = false;
        }
      }

      const hasStoreHours = !!(
        settingsRes.store_open_time && settingsRes.store_close_time
      );

      const hasEmployees = Array.isArray(employeesRes) && employeesRes.length > 0;

      setChecks({
        hasMyData: realMyData,
        hasStoreHours,
        hasEmployees,
        loading: false,
      });
    } catch (err) {
      console.error('Onboarding check error:', err);
      setChecks((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  if (dismissed || checks.loading) return null;

  const items = [
    {
      label: 'Δημιουργία επιχείρησης',
      done: true,
      icon: <StorefrontIcon />,
    },
    {
      label: 'Σύνδεση myDATA',
      done: checks.hasMyData,
      icon: <LinkIcon />,
      action: () => navigate('/settings/mydata'),
      actionLabel: 'Ρύθμιση',
    },
    {
      label: 'Ρύθμιση ωραρίου',
      done: checks.hasStoreHours,
      icon: <AccessTimeIcon />,
      action: () => navigate('/settings/store-hours'),
      actionLabel: 'Ρύθμιση',
    },
    {
      label: 'Προσθήκη προσωπικού',
      done: checks.hasEmployees,
      icon: <PeopleIcon />,
      action: () => navigate('/staff'),
      actionLabel: 'Προσθήκη',
    },
  ];

  const completedCount = items.filter((i) => i.done).length;
  const progress = (completedCount / items.length) * 100;

  if (completedCount === items.length) return null;

  return (
    <Card
      sx={{
        mb: 3,
        border: '1px solid',
        borderColor: 'primary.light',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'primary.main',
          color: 'white',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <RocketLaunchIcon />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Ξεκινήστε με το Palmera
          </Typography>
          <Chip
            label={`${completedCount}/${items.length}`}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {expanded ? (
            <ExpandLessIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
          ) : (
            <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
          )}
        </Box>
      </Box>

      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 4,
          bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': { bgcolor: 'success.main' },
        }}
      />

      <Collapse in={expanded}>
        <CardContent sx={{ px: 3, py: 2 }}>
          <List dense disablePadding>
            {items.map((item, idx) => (
              <ListItem
                key={idx}
                disableGutters
                secondaryAction={
                  !item.done && item.action ? (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={item.action}
                      sx={{ textTransform: 'none' }}
                    >
                      {item.actionLabel}
                    </Button>
                  ) : null
                }
                sx={{
                  py: 1,
                  opacity: item.done ? 0.7 : 1,
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {item.done ? (
                    <CheckCircleIcon sx={{ color: 'success.main' }} />
                  ) : (
                    <RadioButtonUncheckedIcon sx={{ color: 'text.disabled' }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    sx: {
                      textDecoration: item.done ? 'line-through' : 'none',
                      fontWeight: item.done ? 400 : 500,
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Collapse>
    </Card>
  );
}
